import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SaveIndicator } from "@/components/shared/save-indicator";
import { DatePicker } from "@/components/shared/date-picker";
import { useAutoSave } from "@/hooks/use-auto-save";
import { useUpdateKeyResult } from "@/hooks/use-key-results";
import { isTerminalStatus } from "@/lib/status-machine";
import { formatDueDate } from "@/lib/date";
import type { KeyResult, UpdateKeyResultRequest } from "@/types/entities";

interface KeyResultInlineEditorProps {
  keyResult: KeyResult;
  canEdit: boolean;
}

export function KeyResultInlineEditor({ keyResult, canEdit }: KeyResultInlineEditorProps) {
  const [title, setTitle] = useState(keyResult.title);
  const [description, setDescription] = useState(keyResult.description);
  const [dueDate, setDueDate] = useState<string | null>(keyResult.due_date ?? null);
  const updateKeyResult = useUpdateKeyResult(keyResult.id);
  const { saveStatus, debouncedSave, save } = useAutoSave<UpdateKeyResultRequest>({ mutation: updateKeyResult });

  const isEditable = canEdit && !isTerminalStatus(keyResult.status);

  useEffect(() => {
    setTitle(keyResult.title);
    setDescription(keyResult.description);
    setDueDate(keyResult.due_date ?? null);
  }, [keyResult.title, keyResult.description, keyResult.due_date]);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (value.trim()) {
      debouncedSave({ title: value });
    }
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    debouncedSave({ description: value });
  };

  const handleDueDateChange = (value: string | null) => {
    setDueDate(value);
    save({ due_date: value });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label htmlFor="kr-title" className="text-sm font-medium">
          Title
        </Label>
        <SaveIndicator status={saveStatus} />
      </div>

      {isEditable ? (
        <Input
          id="kr-title"
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
        />
      ) : (
        <p className="text-lg font-semibold">{title}</p>
      )}

      <div className="space-y-2">
        <Label htmlFor="kr-desc">Description</Label>
        {isEditable ? (
          <Textarea
            id="kr-desc"
            value={description}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            rows={4}
          />
        ) : (
          <p className="whitespace-pre-wrap text-sm text-[hsl(var(--muted-foreground))]">
            {description || "No description"}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="kr-due-date">Due date</Label>
        {isEditable ? (
          <DatePicker
            id="kr-due-date"
            value={dueDate}
            onChange={handleDueDateChange}
          />
        ) : (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            {dueDate ? formatDueDate(dueDate) : "No due date"}
          </p>
        )}
      </div>
    </div>
  );
}
